<template>
  <div class="builder-container">
    <!-- Top Bar -->
    <TopBar
      :responsive-mode="responsiveMode"
      :can-undo="canUndo"
      :can-redo="canRedo"
      :is-saving="isSaving"
      @change-mode="setResponsiveMode"
      @undo="undo"
      @redo="redo"
      @save="saveStructure"
      @publish="publishStructure"
    />

    <!-- Main Content -->
    <div class="builder-main">
      <!-- Blocks Palette (Left) -->
      <BlocksPalette
        @add-element="addElementToSelected"
      />

      <!-- Canvas (Center) -->
      <BuilderCanvas
        :structure="currentStructure"
        :responsive-mode="responsiveMode"
        :selected-element="selectedElement"
        @select="selectElement"
        @update="updateStructure"
        @delete="deleteElement"
      />

      <!-- Settings Panel (Right) -->
      <SettingsPanel
        :selected-element="selectedElement"
        :structure="currentStructure"
        @update-element="updateElement"
        @update-column="updateColumn"
        @update-row="updateRow"
        @update-global="updateGlobalSettings"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import TopBar from './TopBar.vue';
import BlocksPalette from './BlocksPalette.vue';
import BuilderCanvas from './BuilderCanvas.vue';
import SettingsPanel from './SettingsPanel.vue';
import { useHistory } from '../composables/useHistory';
import { useStructureApi } from '../composables/useStructureApi';

// Structure data
const currentStructure = ref({
  rows: [],
  styles: {
    'background-color': '#ffffff',
    'padding': '0px'
  },
  custom_css: '',
  meta: {
    name: 'Untitled Structure',
    description: ''
  }
});

// History management
const {
  history,
  currentIndex,
  canUndo,
  canRedo,
  pushHistory,
  undo: undoHistory,
  redo: redoHistory
} = useHistory(currentStructure);

// API
const {
  loading: isSaving,
  loadStructure,
  saveStructure: apiSaveStructure,
  publishStructure: apiPublishStructure
} = useStructureApi();

// UI State
const responsiveMode = ref('desktop'); // desktop, tablet, mobile
const selectedElement = ref(null);
const structureId = ref(null);

// Load structure on mount
onMounted(async () => {
  // Get structure ID from data attribute
  const appEl = document.getElementById('ui-builder-app');
  structureId.value = appEl?.getAttribute('data-structure-id');
  
  if (structureId.value && structureId.value !== 'new') {
    const loaded = await loadStructure(structureId.value);
    if (loaded) {
      currentStructure.value = loaded.structure;
      pushHistory();
    }
  } else {
    // New structure - load default or from URL params
    const params = new URLSearchParams(window.location.search);
    const component = params.get('component') || 'footer';
    const name = params.get('name') || 'New Structure';
    const slug = params.get('slug') || 'new-structure';
    
    currentStructure.value = getDefaultStructure();
    currentStructure.value.meta.name = name;
    currentStructure.value.meta.component = component;
    currentStructure.value.meta.slug = slug;
    pushHistory();
  }
});

// Watch for structure changes
watch(() => currentStructure.value, (newVal) => {
  // Debounce auto-save could go here
}, { deep: true });

// Methods
function setResponsiveMode(mode) {
  responsiveMode.value = mode;
}

function selectElement(element) {
  selectedElement.value = element;
}

function addElementToSelected(elementType) {
  if (!selectedElement.value || selectedElement.value.type !== 'column') {
    alert('Please select a column first');
    return;
  }

  const newElement = createDefaultElement(elementType);
  
  // Find column and add element
  for (const row of currentStructure.value.rows) {
    for (const column of row.columns) {
      if (column.id === selectedElement.value.id) {
        if (!column.elements) {
          column.elements = [];
        }
        column.elements.push(newElement);
        pushHistory();
        selectElement(newElement);
        return;
      }
    }
  }
}

function updateStructure(newStructure) {
  currentStructure.value = newStructure;
  pushHistory();
}

function updateElement(elementId, updates) {
  // Find and update element
  for (const row of currentStructure.value.rows) {
    for (const column of row.columns) {
      for (const element of column.elements || []) {
        if (element.id === elementId) {
          Object.assign(element.settings, updates);
          pushHistory();
          return;
        }
      }
    }
  }
}

function updateColumn(columnId, updates) {
  for (const row of currentStructure.value.rows) {
    for (const column of row.columns) {
      if (column.id === columnId) {
        Object.assign(column, updates);
        pushHistory();
        return;
      }
    }
  }
}

function updateRow(rowId, updates) {
  for (const row of currentStructure.value.rows) {
    if (row.id === rowId) {
      Object.assign(row, updates);
      pushHistory();
      return;
    }
  }
}

function updateGlobalSettings(updates) {
  if (updates.styles) {
    Object.assign(currentStructure.value.styles, updates.styles);
  }
  if (updates.custom_css !== undefined) {
    currentStructure.value.custom_css = updates.custom_css;
  }
  if (updates.meta) {
    Object.assign(currentStructure.value.meta, updates.meta);
  }
  pushHistory();
}

function deleteElement(elementId) {
  for (const row of currentStructure.value.rows) {
    for (const column of row.columns) {
      if (column.elements) {
        const index = column.elements.findIndex(el => el.id === elementId);
        if (index !== -1) {
          column.elements.splice(index, 1);
          selectedElement.value = null;
          pushHistory();
          return;
        }
      }
    }
  }
}

function undo() {
  const previous = undoHistory();
  if (previous) {
    currentStructure.value = previous;
    selectedElement.value = null;
  }
}

function redo() {
  const next = redoHistory();
  if (next) {
    currentStructure.value = next;
    selectedElement.value = null;
  }
}

async function saveStructure() {
  const saved = await apiSaveStructure(structureId.value, currentStructure.value);
  if (saved) {
    structureId.value = saved.id;
    alert('Structure saved successfully!');
  }
}

async function publishStructure() {
  if (!structureId.value) {
    alert('Please save the structure first');
    return;
  }
  
  const published = await apiPublishStructure(structureId.value);
  if (published) {
    alert('Structure published successfully!');
  }
}

// Helpers
function getDefaultStructure() {
  return {
    rows: [
      {
        id: 'row_' + Date.now(),
        columns: [
          {
            id: 'col_' + Date.now(),
            width: 12,
            elements: [],
            styles: {}
          }
        ],
        styles: {}
      }
    ],
    styles: {
      'background-color': '#ffffff',
      'padding': '40px 20px'
    },
    custom_css: '',
    meta: {
      name: 'New Structure',
      description: ''
    }
  };
}

function createDefaultElement(type) {
  const id = 'el_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  
  const defaults = {
    text: {
      content: 'Your text here...',
      tag: 'p',
      align: 'left',
      class: ''
    },
    image: {
      src: '',
      alt: 'Image',
      width: '100%',
      link: '',
      class: ''
    },
    links: {
      items: [
        { text: 'Link 1', url: '#' },
        { text: 'Link 2', url: '#' }
      ],
      layout: 'vertical',
      class: ''
    },
    social: {
      networks: [
        { name: 'facebook', url: 'https://facebook.com' },
        { name: 'twitter', url: 'https://twitter.com' }
      ],
      style: 'default',
      class: ''
    },
    menu: {
      menu_slug: 'main-menu',
      class: ''
    },
    newsletter: {
      placeholder: 'Enter your email',
      button_text: 'Subscribe',
      action_url: '/newsletter/subscribe',
      class: ''
    },
    html: {
      content: '<div>Custom HTML</div>',
      class: ''
    },
    divider: {
      height: '1px',
      style: 'solid',
      color: '#e5e7eb',
      class: ''
    },
    spacer: {
      height: '20px'
    }
  };

  return {
    id,
    type,
    settings: defaults[type] || {}
  };
}
</script>

